import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { setSession } = useAuth();
  const navigate = useNavigate();

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password };
      const { data } = await api.post(path, payload);
      await setSession(data);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="h-16 flex items-center px-6 border-b border-slate-200 bg-white">
        <Logo />
      </header>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
              {mode === "login" ? "Sign in" : "Create your account"}
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              {mode === "login" ? "Access your decision research workspace." : "Start researching your decisions with evidence."}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label className="text-sm text-slate-700">Name</Label>
                <Input data-testid="name-input" value={form.name} onChange={upd("name")} required
                  className="mt-1.5 rounded-lg" placeholder="Your name" />
              </div>
            )}
            <div>
              <Label className="text-sm text-slate-700">Email</Label>
              <Input data-testid="email-input" type="email" value={form.email} onChange={upd("email")} required
                className="mt-1.5 rounded-lg" placeholder="you@company.com" />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Password</Label>
              <Input data-testid="password-input" type="password" value={form.password} onChange={upd("password")} required
                className="mt-1.5 rounded-lg" placeholder="••••••••" minLength={6} />
            </div>
            <Button type="submit" disabled={loading} data-testid="auth-submit"
              className="w-full bg-slate-900 hover:bg-slate-800 rounded-lg h-11">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-slate-500 text-center mt-6">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-medium text-slate-900 hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
